import React, { useEffect, useMemo, useState } from "react";
import ManufacturerNav from "../components/ManufacturerNav";
import portalLogisticsApi from "../services/portalLogisticsApi";
import portalMeteredApi from "../services/portalMeteredApi";
import { getStoredPermissions } from "../services/authApi";
import "../styles/PortalBusiness.css";

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function fmtNum(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return String(n);
}

export default function PortalMeteredPage() {
  const perms = getStoredPermissions();

  const canOpen = perms.includes("meter.session.open") || perms.includes("meter.session.read");
  const canRecord = perms.includes("meter.session.record");
  const canClose = perms.includes("meter.session.close");

  const links = useMemo(
    () => [
      { label: "Dashboard", to: "/portal/dashboard", perm: "portal.dashboard.read" },
      { label: "Shipments", to: "/portal/shipments", perm: "logistics.shipments.read" },
      { label: "Metered", to: "/portal/metered", perm: "meter.session.read" },
      { label: "Forecast", to: "/portal/forecast", perm: "analytics.forecast.read" },
      { label: "Compliance", to: "/portal/compliance", perm: "compliance.read" },
    ],
    []
  );

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");

  const [meters, setMeters] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [openForm, setOpenForm] = useState({
    meterId: "",
    startReading: "",
    unitPrice: "",
    taxRate: "",
    notes: "",
  });

  const [rowEdits, setRowEdits] = useState({}); // { [sessionId]: { endReading, quantityDispensed, unitPrice, taxRate, notes } }

  async function loadBranchesSafe() {
    try {
      const b = await portalLogisticsApi.listBranches();
      if (b?.ok) {
        const list = Array.isArray(b.branches) ? b.branches : (Array.isArray(b.rows) ? b.rows : []);
        setBranches(list);
      }
    } catch {
      // ignore (nice-to-have)
    }
  }

  async function loadMetersAndSessions() {
    setLoading(true);
    setErr("");
    try {
      const m = await portalMeteredApi.listMeters({
        branchId: branchId ? Number(branchId) : undefined,
        active: 1,
        limit: 200,
        offset: 0,
      });
      if (!m?.ok) throw new Error(m?.error || "Failed to load meters");
      setMeters(Array.isArray(m.rows) ? m.rows : []);

      const s = await portalMeteredApi.listSessions({
        branchId: branchId ? Number(branchId) : undefined,
        limit: 200,
        offset: 0,
      });
      if (!s?.ok) throw new Error(s?.error || "Failed to load sessions");
      setSessions(Array.isArray(s.rows) ? s.rows : []);
    } catch (e) {
      setErr(e?.message || "Failed to load metered data");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadBranchesSafe();
  }, []);

  useEffect(() => {
    loadMetersAndSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  function setEdit(sessionId, patch) {
    setRowEdits((prev) => ({
      ...prev,
      [sessionId]: { ...(prev[sessionId] || {}), ...patch },
    }));
  }

  async function onOpenSession(e) {
    e.preventDefault();
    setErr("");

    if (!canOpen) return setErr("You do not have permission to open sessions.");

    const meterIdNum = Number(openForm.meterId);
    if (!meterIdNum) return setErr("Select a meter.");

    const meter = meters.find((x) => Number(x.id) === meterIdNum);
    const effectiveBranchId = branchId ? Number(branchId) : Number(meter?.branch_id);

    if (!effectiveBranchId) return setErr("Branch is required (select a branch or ensure meter has branch_id).");

    setLoading(true);
    try {
      const payload = {
        meterId: meterIdNum,
        branchId: effectiveBranchId,
        startReading: openForm.startReading === "" ? null : Number(openForm.startReading),
        unitPrice: openForm.unitPrice === "" ? null : Number(openForm.unitPrice),
        taxRate: openForm.taxRate === "" ? null : Number(openForm.taxRate),
        notes: openForm.notes || null,
      };

      const res = await portalMeteredApi.openSession(payload);
      if (!res?.ok) throw new Error(res?.error || "Failed to open session");

      setOpenForm({ meterId: "", startReading: "", unitPrice: "", taxRate: "", notes: "" });
      await loadMetersAndSessions();
    } catch (e2) {
      setErr(e2?.message || "Failed to open session");
    } finally {
      setLoading(false);
    }
  }

  async function onRecord(session) {
    setErr("");
    if (!canRecord) return setErr("You do not have permission to record dispensing.");

    setLoading(true);
    try {
      const edit = rowEdits[session.id] || {};
      const payload = {
        endReading: edit.endReading === "" || edit.endReading == null ? null : Number(edit.endReading),
        quantityDispensed: edit.quantityDispensed === "" || edit.quantityDispensed == null ? null : Number(edit.quantityDispensed),
        unitPrice: edit.unitPrice === "" || edit.unitPrice == null ? null : Number(edit.unitPrice),
        taxRate: edit.taxRate === "" || edit.taxRate == null ? null : Number(edit.taxRate),
        notes: edit.notes || null,
      };

      const res = await portalMeteredApi.recordSession(session.id, payload);
      if (!res?.ok) throw new Error(res?.error || "Failed to record session");
      await loadMetersAndSessions();
    } catch (e2) {
      setErr(e2?.message || "Failed to record session");
    } finally {
      setLoading(false);
    }
  }

  async function onClose(session) {
    setErr("");
    if (!canClose) return setErr("You do not have permission to close sessions.");

    setLoading(true);
    try {
      const edit = rowEdits[session.id] || {};
      const payload = {
        endReading: edit.endReading === "" || edit.endReading == null ? null : Number(edit.endReading),
        quantityDispensed: edit.quantityDispensed === "" || edit.quantityDispensed == null ? null : Number(edit.quantityDispensed),
        unitPrice: edit.unitPrice === "" || edit.unitPrice == null ? null : Number(edit.unitPrice),
        taxRate: edit.taxRate === "" || edit.taxRate == null ? null : Number(edit.taxRate),
        notes: edit.notes || null,
      };

      const res = await portalMeteredApi.closeSession(session.id, payload);
      if (!res?.ok) throw new Error(res?.error || "Failed to close session");
      await loadMetersAndSessions();
    } catch (e2) {
      setErr(e2?.message || "Failed to close session");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="ucx-section ucx-portal-page">
      <div className="ucx-container">
        <ManufacturerNav
          title="Metered"
          subtitle="Meters and metered sessions (open, record, close)"
          links={links}
        />

        <div className="ucx-portal-panel">
          <div className="ucx-portal-head">
            <div>
              <h1 className="ucx-portal-title">Metered Sessions</h1>
              <p className="ucx-portal-sub">Filter by branch and manage sessions based on your role permissions.</p>
            </div>
            <div className="ucx-portal-actions">
              <button className="ucx-btn ucx-btn-outline" type="button" onClick={loadMetersAndSessions} disabled={loading}>
                Reload
              </button>
            </div>
          </div>

          {err ? <div className="ucx-portal-error">{err}</div> : null}
          {loading ? <div className="ucx-muted">Loading…</div> : null}

          <div className="ucx-portal-card-large">
            <div className="ucx-portal-lineitems-head">
              <div className="ucx-portal-panel-title">Controls</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <span className={`ucx-portal-badge ${canOpen ? "ucx-portal-badge-success" : ""}`}>Open: {canOpen ? "Yes" : "No"}</span>
                <span className={`ucx-portal-badge ${canRecord ? "ucx-portal-badge-success" : ""}`}>Record: {canRecord ? "Yes" : "No"}</span>
                <span className={`ucx-portal-badge ${canClose ? "ucx-portal-badge-success" : ""}`}>Close: {canClose ? "Yes" : "No"}</span>
              </div>
            </div>

            <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              <div className="ucx-portal-field">
                <label>Branch (optional)</label>
                <select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                  <option value="">All / Auto</option>
                  {(branches || []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name} (#{b.id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="ucx-portal-field" style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
                <button className="ucx-btn ucx-btn-primary" type="button" onClick={loadMetersAndSessions} disabled={loading}>
                  Refresh
                </button>
                <button
                  className="ucx-btn ucx-btn-outline"
                  type="button"
                  onClick={() => {
                    setBranchId("");
                    setErr("");
                  }}
                  disabled={loading}
                >
                  Clear
                </button>
              </div>

              <div className="ucx-portal-field" style={{ gridColumn: "span 2" }}>
                <label>Notes</label>
                <div className="ucx-muted" style={{ fontSize: 12, marginTop: 2 }}>
                  Sessions table shows raw DB fields (snake_case) as returned by backend.
                </div>
              </div>
            </div>
          </div>

          <div className="ucx-portal-grid-2" style={{ marginTop: 14 }}>
            <div className="ucx-portal-card-large">
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Meters</div>
                <div className="ucx-portal-badge">Count: {(meters || []).length}</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Branch</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>UOM</th>
                      <th>Active</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(meters || []).length ? (
                      meters.map((m) => (
                        <tr key={m.id}>
                          <td>{m.id}</td>
                          <td>{m.branch_id ?? "-"}</td>
                          <td>{m.name}</td>
                          <td>{m.type}</td>
                          <td>{m.uom}</td>
                          <td>{m.is_active ? "Yes" : "No"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="ucx-muted">
                          No meters found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ucx-portal-lineitems" style={{ marginTop: 14 }}>
                <div className="ucx-portal-lineitems-head">
                  <div className="ucx-portal-panel-title">Open session</div>
                  <div className="ucx-portal-toolbar-hint">Opens a new session on a meter.</div>
                </div>

                <form onSubmit={onOpenSession}>
                  <div className="ucx-portal-form" style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
                    <div className="ucx-portal-field" style={{ gridColumn: "span 2" }}>
                      <label>Meter</label>
                      <select
                        value={openForm.meterId}
                        onChange={(e) => setOpenForm((p) => ({ ...p, meterId: e.target.value }))}
                      >
                        <option value="">Select meter</option>
                        {(meters || []).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name} (#{m.id}) — Branch {m.branch_id}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="ucx-portal-field">
                      <label>Start reading</label>
                      <input
                        value={openForm.startReading}
                        onChange={(e) => setOpenForm((p) => ({ ...p, startReading: e.target.value }))}
                        placeholder="e.g. 0"
                      />
                    </div>

                    <div className="ucx-portal-field">
                      <label>Unit price</label>
                      <input
                        value={openForm.unitPrice}
                        onChange={(e) => setOpenForm((p) => ({ ...p, unitPrice: e.target.value }))}
                        placeholder="optional"
                      />
                    </div>

                    <div className="ucx-portal-field">
                      <label>Tax rate</label>
                      <input
                        value={openForm.taxRate}
                        onChange={(e) => setOpenForm((p) => ({ ...p, taxRate: e.target.value }))}
                        placeholder="optional"
                      />
                    </div>

                    <div className="ucx-portal-field" style={{ gridColumn: "span 3" }}>
                      <label>Notes</label>
                      <input
                        value={openForm.notes}
                        onChange={(e) => setOpenForm((p) => ({ ...p, notes: e.target.value }))}
                        placeholder="optional"
                      />
                    </div>
                  </div>

                  <div className="ucx-portal-actions" style={{ marginTop: 10, justifyContent: "flex-start" }}>
                    <button className="ucx-btn ucx-btn-primary" type="submit" disabled={loading || !canOpen}>
                      Open Session
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="ucx-portal-card-large">
              <div className="ucx-portal-lineitems-head">
                <div className="ucx-portal-panel-title">Sessions</div>
                <div className="ucx-portal-badge">Count: {(sessions || []).length}</div>
              </div>

              <div className="ucx-portal-tablewrap">
                <table className="ucx-portal-table" style={{ minWidth: 1200 }}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Meter</th>
                      <th>Branch</th>
                      <th>Status</th>
                      <th>Opened</th>
                      <th>Closed</th>
                      <th style={{ textAlign: "right" }}>Qty</th>
                      <th style={{ textAlign: "right" }}>End reading</th>
                      <th style={{ textAlign: "right" }}>Unit price</th>
                      <th style={{ textAlign: "right" }}>Tax rate</th>
                      <th>Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sessions || []).length ? (
                      sessions.map((s) => {
                        const edit = rowEdits[s.id] || {};
                        const isOpen = String(s.status || "").toLowerCase() === "open";

                        return (
                          <tr key={s.id}>
                            <td>{s.id}</td>
                            <td>{s.meter_id ?? "-"}</td>
                            <td>{s.branch_id ?? "-"}</td>
                            <td>{s.status ?? "-"}</td>
                            <td>{fmtDate(s.opened_at)}</td>
                            <td>{fmtDate(s.closed_at)}</td>

                            <td style={{ textAlign: "right" }}>
                              {isOpen ? (
                                <input
                                  className="ucx-portal-input-compact"
                                  value={edit.quantityDispensed ?? ""}
                                  onChange={(e) => setEdit(s.id, { quantityDispensed: e.target.value })}
                                  placeholder={fmtNum(s.quantity_dispensed ?? "") || "0"}
                                />
                              ) : (
                                s.quantity_dispensed ?? 0
                              )}
                            </td>

                            <td style={{ textAlign: "right" }}>
                              {isOpen ? (
                                <input
                                  className="ucx-portal-input-compact"
                                  value={edit.endReading ?? ""}
                                  onChange={(e) => setEdit(s.id, { endReading: e.target.value })}
                                  placeholder={fmtNum(s.end_reading ?? "")}
                                />
                              ) : (
                                s.end_reading ?? "-"
                              )}
                            </td>

                            <td style={{ textAlign: "right" }}>
                              {isOpen ? (
                                <input
                                  className="ucx-portal-input-compact"
                                  value={edit.unitPrice ?? ""}
                                  onChange={(e) => setEdit(s.id, { unitPrice: e.target.value })}
                                  placeholder={fmtNum(s.unit_price ?? "")}
                                />
                              ) : (
                                s.unit_price ?? "-"
                              )}
                            </td>

                            <td style={{ textAlign: "right" }}>
                              {isOpen ? (
                                <input
                                  className="ucx-portal-input-compact"
                                  value={edit.taxRate ?? ""}
                                  onChange={(e) => setEdit(s.id, { taxRate: e.target.value })}
                                  placeholder={fmtNum(s.tax_rate ?? "")}
                                />
                              ) : (
                                s.tax_rate ?? "-"
                              )}
                            </td>

                            <td style={{ maxWidth: 220 }}>
                              {isOpen ? (
                                <input
                                  className="ucx-portal-input-compact"
                                  value={edit.notes ?? ""}
                                  onChange={(e) => setEdit(s.id, { notes: e.target.value })}
                                  placeholder={s.notes ?? "optional"}
                                />
                              ) : (
                                <span className="ucx-muted">{s.notes || "—"}</span>
                              )}
                            </td>

                            <td style={{ whiteSpace: "nowrap" }}>
                              {isOpen ? (
                                <div className="ucx-portal-row-actions">
                                  <button
                                    className="ucx-btn ucx-btn-outline"
                                    type="button"
                                    onClick={() => onRecord(s)}
                                    disabled={loading || !canRecord}
                                  >
                                    Record
                                  </button>
                                  <button
                                    className="ucx-btn ucx-btn-primary"
                                    type="button"
                                    onClick={() => onClose(s)}
                                    disabled={loading || !canClose}
                                  >
                                    Close
                                  </button>
                                </div>
                              ) : (
                                <span className="ucx-muted">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={12} className="ucx-muted">
                          No sessions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ucx-portal-toolbar" style={{ marginTop: 10 }}>
                <div className="ucx-portal-toolbar-hint">Tip: Use Branch filter to narrow results when the list grows.</div>
                <div />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
